import { useCallback, useSyncExternalStore } from 'react';

// localStorage that never throws (private mode, blocked storage, quota) and keeps every
// component reading the same key in sync. Values are validated on read, so a corrupted
// or old-shaped entry falls back to the default instead of crashing the page.

type Listener = () => void;
const listeners = new Map<string, Set<Listener>>();
const memory = new Map<string, unknown>();

function readRaw(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function readStored<T>(key: string, fallback: T, validate: (v: unknown) => v is T): T {
  if (memory.has(key)) return memory.get(key) as T;
  let value = fallback;
  const raw = readRaw(key);
  if (raw != null) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (validate(parsed)) value = parsed;
    } catch {
      /* ignore corrupt entry */
    }
  }
  memory.set(key, value);
  return value;
}

export function writeStored<T>(key: string, value: T) {
  memory.set(key, value);
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage blocked: keep the in-memory value for this session */
  }
  listeners.get(key)?.forEach((l) => l());
}

export function removeStored(key: string) {
  memory.delete(key);
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
  listeners.get(key)?.forEach((l) => l());
}

function subscribe(key: string, l: Listener) {
  let set = listeners.get(key);
  if (!set) listeners.set(key, (set = new Set()));
  set.add(l);
  return () => set.delete(l);
}

// Another tab changed storage: drop our cached copy and notify.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (!e.key || !memory.has(e.key)) return;
    memory.delete(e.key);
    listeners.get(e.key)?.forEach((l) => l());
  });
}

export function useStored<T>(
  key: string,
  fallback: T,
  validate: (v: unknown) => v is T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const value = useSyncExternalStore(
    useCallback((l) => subscribe(key, l), [key]),
    () => readStored(key, fallback, validate),
    () => fallback,
  );
  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      const prev = readStored(key, fallback, validate);
      writeStored(key, typeof next === 'function' ? (next as (p: T) => T)(prev) : next);
    },
    // fallback and validate are module-level constants at every call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );
  return [value, set];
}

/** For tests. */
export function __resetStorageCache() {
  memory.clear();
}

export const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');
